import { App } from "@/components/app"
import { preloadPage } from "@/pages"
import { startInteractionCounter } from "@/utilities/interactionCounter"
import { setupStorageDevProxy } from "@/utilities/setupStorageDevProxy"
import { createRoot, hydrateRoot } from "react-dom/client"
import "./styles/ant-message.less"
import "./styles/check-box.less"
import "./styles/drawer.less"
import "./styles/form-elements.less"
import "./styles/global.less"
import "./styles/mixins/scroll-bar.less"
import "./styles/modal.less"
import "./styles/popovers.less"
import "./styles/sider-menu.less"
import "./styles/tables.less"
import "./styles/tabs.less"

setupStorageDevProxy()
startInteractionCounter()

// Suppress console errors for blocked third-party tracking scripts (ad blockers)
// These errors are harmless and expected when users have ad blockers enabled
const originalConsoleError = console.error
console.error = function (...args: any[]) {
  // Check if error is from blocked tracking scripts
  const errorMessage = args.join(" ").toLowerCase()
  const isBlockedTrackingError =
    errorMessage.includes("px.ads.linkedin.com") ||
    errorMessage.includes("insight_tag_errors") ||
    errorMessage.includes("err_blocked_by_client") ||
    (errorMessage.includes("failed to load resource") &&
      (errorMessage.includes("linkedin") || errorMessage.includes("analytics") || errorMessage.includes("tracking")))

  // Suppress blocked tracking errors (they're harmless)
  if (isBlockedTrackingError) {
    return // Don't log these errors
  }

  // Log all other errors normally
  originalConsoleError.apply(console, args)
}

// Handle chunk loading errors that occur after deployments
// When old chunks are referenced but no longer exist, automatically reload the page
const isChunkLoadError = (error: Error | Event): boolean => {
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase()
    const errorName = error.name.toLowerCase()
    const errorString = String(error).toLowerCase()

    // Check for common chunk loading error patterns
    const allErrorText = `${errorName} ${errorMessage} ${errorString}`.toLowerCase()

    return (
      allErrorText.includes("chunk") ||
      allErrorText.includes("loading chunk") ||
      allErrorText.includes("failed to fetch dynamically imported module") ||
      allErrorText.includes("importing a module script failed") ||
      allErrorText.includes("dynamically imported") ||
      allErrorText.includes("/assets/")
    )
  }

  if (error instanceof Event) {
    const target = error.target as HTMLElement | null
    if (target) {
      // Only check for script/link errors that are actual chunks (in /assets/ directory)
      // Don't catch all script/link errors - only chunk-related ones
      if (target.tagName === "SCRIPT") {
        const scriptElement = target as HTMLScriptElement
        const src = scriptElement.src || ""
        // Only treat as chunk error if it's in /assets/ and is a JS file
        if (src.includes("/assets/") && src.endsWith(".js")) {
          return true
        }
        // Not a chunk error - could be third-party script, analytics, etc.
        return false
      }
      if (target.tagName === "LINK") {
        const linkElement = target as HTMLLinkElement
        const href = linkElement.href || ""
        // Only treat as chunk error if it's in /assets/ and is a CSS file
        if (href.includes("/assets/") && href.endsWith(".css")) {
          return true
        }
        // Not a chunk error - could be third-party stylesheet, etc.
        return false
      }
    }
  }

  return false
}

// Global error handler for chunk loading errors
const handleChunkLoadError = () => {
  // Only reload if we haven't already tried recently (prevent reload loops)
  const lastReloadAttempt = sessionStorage.getItem("chunkReloadAttempt")
  const reloadCount = parseInt(sessionStorage.getItem("chunkReloadCount") || "0", 10)
  const now = Date.now()

  // Maximum retry limit - prevent infinite loops
  const MAX_RELOAD_ATTEMPTS = 2
  if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
    console.warn("Chunk reload limit reached. Stopping automatic reloads.")
    sessionStorage.removeItem("chunkReloadAttempt")
    sessionStorage.removeItem("chunkReloadCount")
    return
  }

  if (lastReloadAttempt) {
    const timeSinceLastAttempt = now - parseInt(lastReloadAttempt, 10)
    // If we tried to reload less than 5 seconds ago, don't try again
    if (timeSinceLastAttempt < 5000) {
      return
    }
  }

  // Increment reload count
  sessionStorage.setItem("chunkReloadCount", (reloadCount + 1).toString())

  // Mark that we're attempting a reload
  sessionStorage.setItem("chunkReloadAttempt", now.toString())

  // Clear the flags after 30 seconds to allow future reloads if needed
  setTimeout(() => {
    sessionStorage.removeItem("chunkReloadAttempt")
    sessionStorage.removeItem("chunkReloadCount")
  }, 30000)

  // Reload the page to get the latest chunks
  window.location.reload()
}

// Listen for script loading errors (chunk loading failures)
window.addEventListener(
  "error",
  event => {
    // Only handle resource loading errors for actual chunk files
    // Don't catch all errors - be very specific to avoid false positives
    if (event.target) {
      const target = event.target as HTMLElement

      // Check for script tag errors - only for /assets/ JS files
      if (target.tagName === "SCRIPT") {
        const scriptElement = target as HTMLScriptElement
        const src = scriptElement.src || ""

        // Ignore third-party domains (analytics, ads, etc.)
        if (src && !src.startsWith(window.location.origin) && !src.startsWith("/")) {
          return // Not our domain, ignore
        }

        // Only treat as chunk error if it's in /assets/ and is a JS file
        // This prevents catching third-party scripts, analytics, etc.
        if (src && src.includes("/assets/") && src.endsWith(".js")) {
          // Only reload if it's actually a failed resource load (404, network error, etc.)
          // Resource loading failures typically don't have an error property
          // Syntax/runtime errors will have an error property
          if (!event.error) {
            event.preventDefault()
            handleChunkLoadError()
            return
          }
        }
      }

      // Check for link tag errors (CSS chunks) - only for /assets/ CSS files
      if (target.tagName === "LINK") {
        const linkElement = target as HTMLLinkElement
        const href = linkElement.href || ""

        // Ignore third-party domains (analytics, ads, etc.)
        if (href && !href.startsWith(window.location.origin) && !href.startsWith("/")) {
          return // Not our domain, ignore
        }

        // Only treat as chunk error if it's in /assets/ and is a CSS file
        if (href && href.includes("/assets/") && href.endsWith(".css")) {
          // Only reload if it's actually a failed resource load (404, network error, etc.)
          // Resource loading failures typically don't have an error property
          if (!event.error) {
            event.preventDefault()
            handleChunkLoadError()
            return
          }
        }
      }

      // Ignore IMG and other resource types (like LinkedIn tracking pixels)
      if (target.tagName === "IMG" || (target.tagName !== "SCRIPT" && target.tagName !== "LINK")) {
        return // Not a script or link, ignore
      }
    }

    // Only check isChunkLoadError for Error objects with specific chunk error messages
    // This prevents false positives from other error types
    if (event.error && event.error instanceof Error && isChunkLoadError(event.error)) {
      event.preventDefault()
      handleChunkLoadError()
    }
  },
  true
)

// Listen for unhandled promise rejections (common for dynamic imports)
window.addEventListener("unhandledrejection", event => {
  const error = event.reason

  // Be very specific - only catch actual chunk loading errors
  // Don't catch generic "failed to fetch" or "/assets/" errors
  if (error) {
    const errorString = String(error).toLowerCase()
    // Only catch if it explicitly mentions "dynamically imported module"
    // AND contains "/assets/" to ensure it's a chunk, not a regular API call
    if (errorString.includes("failed to fetch dynamically imported module") && errorString.includes("/assets/")) {
      event.preventDefault()
      handleChunkLoadError()
      return
    }
  }

  // Use the isChunkLoadError function for Error objects
  if (error && error instanceof Error && isChunkLoadError(error)) {
    event.preventDefault()
    handleChunkLoadError()
  }
})

const rootElement = document.querySelector<HTMLElement>("#root")
if (!rootElement) throw new Error("Failed to find the root element")

// A public page prerendered at build time (utilities/pageMetaPrerenderPlugin.ts) arrives with its HTML in #root:
// React takes it over instead of drawing the page again. Every other page starts from an empty #root.
if (rootElement.dataset.prerenderWidth) {
  // The page's code loads first, so React hydrates the lazy page in one go. Left half-hydrated, the page would be
  // thrown away and drawn again by the first context update (error #421). A failed load is the lazy page's to report.
  void preloadPage(window.location.pathname)
    .catch(() => undefined)
    .then(() => hydrateRoot(rootElement, <App />))
} else {
  createRoot(rootElement).render(<App />)
}
